import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle2, Loader2, FileText, ChevronRight, Award, Lightbulb, Map as MapIcon, Sparkles, AlertCircle, Bot, ChevronDown, X } from 'lucide-react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { saveTestResult, getResults } from '../lib/testResults';

const API = process.env.REACT_APP_BACKEND_URL;
// QuizEngine pathway slug → backend track key. Both pathways are part of the
// after-10th / inter clarity quiz, which is the only quiz capped at 2 attempts.
const SERVER_TRACK = { btech: 'btech', management: 'bba_mba' };

// Map our URL slugs → test_id in each JSON package.
const PATHWAY_CONFIG = {
  btech: {
    jsonPath: '/data/btech_quiz_package.json',
    label: 'B.Tech',
    slugToTestId: {
      t1: 'T1_BTECH_BRANCH_INTEREST_MAP_15Q',
      t2: 'T2_BTECH_BRANCH_FIT_DEEP_DIVE_15Q',
      t3: 'T3_AI_DIGITAL_AND_SKILL_READINESS_15Q',
      t4: 'T4_BTECH_FUTURE_PATHWAYS_AND_SHORTLIST_15Q',
    },
    testLabel: {
      t1: 'Test 1: Interest Map',
      t2: 'Test 2: Branch Fit Deep Dive',
      t3: 'Test 3: AI-Ready Skills Audit',
      t4: 'Test 4: Core Engineering Future Pathways',
    },
  },
  management: {
    jsonPath: '/data/bba_mba_quiz_package.json',
    label: 'Management (BBA/MBA)',
    slugToTestId: {
      t1: 'T1_BBA_MBA_INTEREST_MAP_AFTER_10TH_INTER',
      t2: 'T2_BBA_MBA_ALL_BRANCH_SPECIALIZATION_FIT',
      t3: 'T3_AI_READY_BUSINESS_SKILLS_AUDIT_ALL_BRANCHES',
      t4: 'T4_BBA_MBA_FUTURE_PATHWAYS_ALL_BRANCHES',
    },
    testLabel: {
      t1: 'Test 1: Business Interest Map',
      t2: 'Test 2: Specialization Fit Deep Dive',
      t3: 'Test 3: AI-Ready Business Skills',
      t4: 'Test 4: Business & Management Future Pathways',
    },
  },
};

const TEST_ORDER = ['t1', 't2', 't3', 't4'];

// Storage key derived from pathway + slug.
const storageKeyFor = (pathway, slug) => `${pathway}-${slug}`;

const QuizEngine = () => {
  const { pathway, slug } = useParams();
  const navigate = useNavigate();
  const cfg = PATHWAY_CONFIG[pathway];
  const testId = cfg?.slugToTestId[slug];

  const [pkg, setPkg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [qIdx, setQIdx] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResult, setShowResult] = useState(false);
  // After-10th clarity quiz cap (2 attempts per track, user-scoped on server).
  // null until first fetch resolves; { used, max } once loaded.
  const [attempts, setAttempts] = useState(null);

  // Fetch user's attempts for this track on mount (cap = 2 per track).
  useEffect(() => {
    const track = SERVER_TRACK[pathway];
    if (!track) return;
    axios.get(`${API}/api/paths/after_10th_inter/attempts?track=${track}`)
      .then((r) => setAttempts(r.data))
      .catch(() => setAttempts({ used: 0, max: 2 }));
  }, [pathway]);

  // Load the JSON package once per pathway.
  useEffect(() => {
    if (!cfg) return;
    let cancelled = false;
    setLoading(true);
    fetch(cfg.jsonPath)
      .then(r => { if (!r.ok) throw new Error('Failed to load quiz data'); return r.json(); })
      .then(data => { if (!cancelled) { setPkg(data); setLoading(false); } })
      .catch(e => { if (!cancelled) { setError(e.message); setLoading(false); } });
    return () => { cancelled = true; };
  }, [cfg]);

  // Reset state when the slug or pathway changes.
  useEffect(() => {
    setQIdx(0);
    setAnswers({});
    setShowResult(false);
  }, [slug, pathway]);

  const test = useMemo(() => pkg?.tests?.find(t => t.test_id === testId), [pkg, testId]);

  // Compute totals from selected options.
  const { tagTotals, sortedTags } = useMemo(() => {
    const totals = {};
    Object.values(answers).forEach(opt => {
      (opt.score_tags || []).forEach(({ tag, points }) => { totals[tag] = (totals[tag] || 0) + points; });
    });
    const sorted = Object.entries(totals).sort((a, b) => b[1] - a[1]);
    return { tagTotals: totals, sortedTags: sorted };
  }, [answers]);

  // Persist result + advance to result screen when all questions answered.
  useEffect(() => {
    if (!showResult || !test) return;
    const primaryTag = sortedTags[0]?.[0] || 'EXPLORER_GENERAL';
    const secondaryTag = sortedTags[1]?.[0] || null;
    const primaryProfile = test.result_profiles.find(r => r.primary_tag === primaryTag) || test.result_profiles[test.result_profiles.length - 1];
    const secondaryProfile = secondaryTag ? test.result_profiles.find(r => r.primary_tag === secondaryTag) : null;
    saveTestResult(pathway, storageKeyFor(pathway, slug), {
      testId,
      title: test.title,
      primaryTag,
      secondaryTag,
      primaryTitle: primaryProfile?.title,
      secondaryTitle: secondaryProfile?.title,
      tagTotals,
    });
    // Server-side cap: ONLY the final test (t4) counts as a full attempt.
    // Sub-tests t1/t2/t3 just save locally. Viewing an already-saved result
    // does NOT consume an attempt (we re-render from localStorage on revisit).
    const serverTrack = SERVER_TRACK[pathway];
    if (serverTrack && slug === 't4') {
      axios.post(`${API}/api/paths/after_10th_inter/submit`, {
        track: serverTrack,
        answers,
        result_profile: { tag: primaryTag, title: primaryProfile?.title || '' },
      }).then((r) => {
        setAttempts((a) => ({ ...(a || {}), used: r.data.attempt_no || 1 }));
      }).catch(() => { /* 429 means already capped — UI handles via attempts state */ });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showResult]);

  if (!cfg || !slug || !cfg.slugToTestId[slug]) {
    return <ErrorBox msg="Unknown test. Pick one from the After 10th/Inter page." />;
  }
  if (loading) {
    return <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-teal-50 to-blue-50"><Loader2 className="animate-spin text-teal-600" size={32} /></div>;
  }
  if (error || !test) {
    return <ErrorBox msg={error || 'Test not found in package.'} />;
  }
  // After-10th clarity cap: block the FINAL test (t4) when both attempts used.
  // Sub-tests (t1/t2/t3) remain free to re-take — they don't increment the
  // counter. Only completing t4 counts as one attempt.
  if (slug === 't4' && attempts && attempts.used >= attempts.max && !showResult) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 py-8 px-4 md:px-6">
        <div className="max-w-2xl mx-auto bg-white border border-amber-200 rounded-3xl p-8 text-center shadow-sm" data-testid="quiz-attempts-blocked">
          <Award className="w-10 h-10 text-amber-600 mx-auto mb-3" />
          <h1 className="text-2xl font-black text-amber-900 mb-2">You&apos;ve used all attempts</h1>
          <p className="text-sm text-amber-800 mb-5">
            The Career Clarity quiz is capped at <strong>{attempts.max} attempts</strong> per track
            to keep your result meaningful. Your previous report is still saved.
          </p>
          <Button asChild className="bg-amber-600 hover:bg-amber-700 text-white">
            <Link to="/dashboard">Back to dashboard</Link>
          </Button>
        </div>
      </div>
    );
  }

  // ────────── Result screen ──────────
  if (showResult) {
    const primaryTag = sortedTags[0]?.[0] || 'EXPLORER_GENERAL';
    const secondaryTag = sortedTags[1]?.[0] || null;
    const primary = test.result_profiles.find(r => r.primary_tag === primaryTag) || test.result_profiles[test.result_profiles.length - 1];
    const secondary = secondaryTag ? test.result_profiles.find(r => r.primary_tag === secondaryTag) : null;
    const nextSlug = TEST_ORDER[TEST_ORDER.indexOf(slug) + 1];

    return (
      <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50 py-8 px-4 md:px-6" data-testid={`result-${slug}`}>
        <div className="max-w-2xl mx-auto space-y-5">
          <div className="text-center">
            <div className="w-16 h-16 bg-teal-100 rounded-full flex items-center justify-center mb-3 mx-auto">
              <CheckCircle2 className="text-teal-600" size={32} />
            </div>
            <Badge className="bg-white text-teal-700 border border-teal-200 mb-2">{cfg.testLabel[slug]}</Badge>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Your Direction</h1>
            <p className="text-sm text-gray-600 mt-1">{test.subtitle}</p>
          </div>

          {/* Primary result */}
          <div className="bg-white rounded-2xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center gap-2 mb-3">
              <Award className="text-teal-600" size={18} />
              <p className="text-xs font-bold text-teal-600 tracking-wider uppercase">Primary Match</p>
            </div>
            <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">{primary.title}</h2>
            {primary.you_may_enjoy && <p className="text-sm text-gray-700 mb-3"><span className="font-semibold">You may enjoy: </span>{primary.you_may_enjoy}</p>}
            {primary.future_guidance && (
              <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-3">
                <div className="flex gap-2"><Sparkles size={14} className="text-blue-600 flex-shrink-0 mt-0.5" /><p className="text-xs text-blue-800 leading-relaxed">{primary.future_guidance}</p></div>
              </div>
            )}
            {(primary.best_fit_branches || primary.branches) && (
              <div className="mb-3">
                <p className="text-xs font-bold text-gray-600 mb-1.5 flex items-center gap-1.5"><MapIcon size={13} />Best-fit branches</p>
                <div className="flex flex-wrap gap-1.5">
                  {(primary.best_fit_branches || primary.branches).map(b => <Badge key={b} className="bg-teal-50 text-teal-700 border border-teal-200">{b}</Badge>)}
                </div>
              </div>
            )}
            {primary.starter_projects && (
              <div>
                <p className="text-xs font-bold text-gray-600 mb-1.5 flex items-center gap-1.5"><Lightbulb size={13} />Starter projects to try</p>
                <ul className="space-y-1">
                  {(Array.isArray(primary.starter_projects) ? primary.starter_projects : [primary.starter_projects]).map((p, i) => (
                    <li key={i} className="text-xs text-gray-700 flex items-start gap-1.5"><ChevronRight size={11} className="text-teal-500 mt-0.5 flex-shrink-0" />{p}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          {/* Secondary result (lighter card) */}
          {secondary && (
            <div className="bg-white/70 rounded-2xl p-5 border border-gray-200">
              <p className="text-xs font-bold text-blue-600 tracking-wider uppercase mb-1">Secondary Match</p>
              <h3 className="text-base font-bold text-gray-900 mb-1">{secondary.title}</h3>
              {secondary.you_may_enjoy && <p className="text-xs text-gray-600">You may also enjoy: {secondary.you_may_enjoy}</p>}
            </div>
          )}

          {/* Score breakdown */}
          <div className="bg-white rounded-2xl p-5 border border-gray-200 shadow-sm">
            <p className="text-xs font-bold text-gray-700 mb-3">Your Score Breakdown</p>
            <div className="space-y-2">
              {sortedTags.slice(0, 5).map(([tag, score]) => {
                const profile = test.result_profiles.find(r => r.primary_tag === tag);
                const max = sortedTags[0]?.[1] || 1;
                return (
                  <div key={tag} className="flex items-center gap-2">
                    <span className="text-[11px] text-gray-700 w-32 md:w-44 truncate flex-shrink-0">{profile?.title || tag}</span>
                    <div className="flex-1 bg-gray-100 rounded-full h-2.5"><div className="bg-teal-500 h-2.5 rounded-full transition-all" style={{ width: `${(score / max) * 100}%` }} /></div>
                    <span className="text-[11px] font-mono text-gray-600 w-6 text-right">{score}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* AI / Leadership add-ons (T4 has these, T3 may use threshold notes too) */}
          {test.add_ons?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">
              <p className="text-xs font-bold text-amber-900 mb-2 flex items-center gap-1.5"><Sparkles size={13} />Add-on guidance</p>
              <div className="space-y-2">
                {test.add_ons.map(a => {
                  // Show add-on if its tag scored ≥ 30% of max (threshold heuristic).
                  const m = a.condition?.match(/^([A-Z_]+)/);
                  const tag = m?.[1];
                  const hit = tag && (tagTotals[tag] || 0) >= 0.3 * (sortedTags[0]?.[1] || 1);
                  if (!hit) return null;
                  return <p key={a.id} className="text-[11px] text-amber-800 leading-relaxed">• {a.text.split('\n')[0]}</p>;
                })}
              </div>
            </div>
          )}

          {/* AI Awareness — full guidance on the result screen */}
          <AIAwarenessResultSection
            perTest={test.ai_awareness_info_box}
            topLevel={pkg.ai_awareness_and_integration_guidance || pkg.ai_awareness_view}
            pathway={pathway}
          />

          {/* Disclaimer */}
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
            <div className="flex gap-2"><AlertCircle size={13} className="text-gray-500 flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-gray-600 leading-relaxed">This quiz is for guidance, not a final career label. LearnixTa can make mistakes — reverify outside too. Use it with school counsellors and parents alongside college quality, fees, placements, and personal circumstances.</p>
            </div>
          </div>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-3 pt-2">
            {nextSlug ? (
              <Button data-testid="next-test-btn" onClick={() => navigate(`/tests/${pathway}/${nextSlug}`)} className="flex-1 btn-primary">
                Continue to {cfg.testLabel[nextSlug].split(':')[0]} <ChevronRight size={14} className="ml-1" />
              </Button>
            ) : (
              <Button data-testid="all-tests-done-btn" onClick={() => navigate('/decision-report')} className="flex-1 btn-primary">
                View Final Decision Report <FileText size={14} className="ml-2" />
              </Button>
            )}
            <Button data-testid="view-report-now-btn" onClick={() => navigate('/decision-report')} variant="outline" className="flex-1">
              <FileText size={14} className="mr-1.5" /> View Decision Report
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // ────────── Question screen ──────────
  const q = test.questions[qIdx];
  const progress = ((qIdx + 1) / test.questions.length) * 100;
  const allAnsweredCount = Object.keys(answers).length;
  const showStage = q.stage_heading && (qIdx === 0 || test.questions[qIdx - 1].stage_heading !== q.stage_heading);

  const onAnswer = (opt) => {
    const next = { ...answers, [q.id]: opt };
    setAnswers(next);
    if (qIdx < test.questions.length - 1) {
      setTimeout(() => setQIdx(qIdx + 1), 180);
    } else {
      setTimeout(() => setShowResult(true), 220);
    }
  };

  const goBack = () => {
    if (qIdx > 0) setQIdx(qIdx - 1);
    else navigate('/pathways/after-10th#tests');
  };

  return (
    <div className="min-h-screen bg-white flex flex-col" data-testid={`quiz-${slug}`}>
      {/* Header with progress */}
      <div className="bg-white shadow-sm px-4 py-3 sticky top-0 z-10">
        <div className="flex items-center justify-between mb-2">
          <button onClick={goBack} className="p-2 -ml-2 text-gray-600 hover:text-gray-900" data-testid="quiz-back-btn"><ArrowLeft size={20} /></button>
          <p className="text-xs font-medium text-gray-700">Question {qIdx + 1} of {test.questions.length}</p>
          <div className="w-9" />
        </div>
        <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
          <div className="h-full bg-teal-600 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
        </div>
      </div>

      <div className="px-5 pt-4 max-w-2xl w-full mx-auto">
        <p className="text-xs text-teal-600 font-semibold">After 10th/Inter — {cfg.label}</p>
        <h1 className="text-base md:text-lg font-bold text-gray-900">{cfg.testLabel[slug]}</h1>
      </div>

      <div className="flex-1 flex flex-col px-5 py-6 max-w-2xl w-full mx-auto">
        {/* AI Awareness Info Card — shown only on question 1 */}
        {qIdx === 0 && (
          test.ai_awareness_info_box && typeof test.ai_awareness_info_box === 'object' ? (
            <AIAwarenessInlineCard data={test.ai_awareness_info_box} />
          ) : pkg.ai_awareness_view ? (
            <AIAwarenessInlineCard data={{ title: pkg.ai_awareness_view.title, content: pkg.ai_awareness_view.intro }} />
          ) : null
        )}

        {showStage && (
          <div className="mb-4 inline-flex self-start">
            <Badge className="bg-blue-50 text-blue-700 border border-blue-200 text-[10px]">{q.stage_heading}</Badge>
          </div>
        )}
        <div className="flex-1 flex items-start">
          <p className="text-lg md:text-2xl font-semibold text-gray-900 leading-snug" data-testid="quiz-question">{q.question_text}</p>
        </div>

        <div className="space-y-2.5 pb-6 pt-4" data-testid="quiz-options">
          {q.options.map((opt) => {
            const selected = answers[q.id]?.option_id === opt.option_id;
            return (
              <button
                key={opt.option_id}
                data-testid={`quiz-option-${opt.option_id}`}
                onClick={() => onAnswer(opt)}
                className={`w-full text-left p-4 rounded-2xl border-2 transition-all active:scale-[0.99] ${
                  selected ? 'border-teal-600 bg-teal-50' : 'border-gray-200 hover:border-teal-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex gap-3 items-start">
                  <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${selected ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-600'}`}>{opt.option_id}</div>
                  <p className="text-sm md:text-base text-gray-800 leading-snug">{opt.text}</p>
                </div>
              </button>
            );
          })}
        </div>

        {allAnsweredCount > 0 && qIdx > 0 && (
          <p className="text-center text-[10px] text-gray-400 pb-3">Auto-saves as you go · Tap back arrow to revise</p>
        )}
      </div>
    </div>
  );
};

const ErrorBox = ({ msg }) => (
  <div className="min-h-screen flex items-center justify-center px-6 bg-gradient-to-br from-teal-50 to-blue-50">
    <div className="text-center max-w-sm">
      <p className="text-red-600 font-semibold mb-2">Could not load test</p>
      <p className="text-sm text-gray-600 mb-4">{msg}</p>
      <Button asChild><Link to="/pathways/after-10th#tests">Back to Tests</Link></Button>
    </div>
  </div>
);

/* ─── AI Awareness — inline card shown above question 1 ─── */
const AIAwarenessInlineCard = ({ data }) => {
  const [dismissed, setDismissed] = useState(false);
  if (dismissed) return null;
  return (
    <div className="mb-5 p-4 rounded-2xl border-2 border-indigo-200 bg-gradient-to-br from-indigo-50 via-blue-50 to-cyan-50 relative" data-testid="ai-awareness-inline">
      <button
        onClick={() => setDismissed(true)}
        className="absolute top-2 right-2 text-indigo-400 hover:text-indigo-700"
        aria-label="Dismiss"
        data-testid="ai-awareness-dismiss"
      >
        <X size={14} />
      </button>
      <div className="flex items-start gap-3 pr-6">
        <div className="w-9 h-9 rounded-xl bg-indigo-100 flex items-center justify-center shrink-0">
          <Bot size={18} className="text-indigo-700" />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] font-bold tracking-widest text-indigo-700 uppercase mb-0.5">AI Awareness — Read before you start</p>
          <p className="text-sm font-bold text-gray-900 mb-1">{data.title}</p>
          <p className="text-xs text-gray-700 leading-relaxed">{data.content}</p>
        </div>
      </div>
    </div>
  );
};

/* ─── AI Awareness — full guidance shown on the result screen ─── */
const AIAwarenessResultSection = ({ perTest, topLevel, pathway }) => {
  const [expanded, setExpanded] = useState(false);
  if (!perTest && !topLevel) return null;
  const isBba = pathway === 'management';
  return (
    <div className="bg-white border-2 border-indigo-200 rounded-2xl p-5 shadow-sm" data-testid="ai-awareness-result-section">
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center shrink-0">
          <Bot size={20} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-bold tracking-widest text-indigo-700 uppercase mb-0.5">AI Awareness</p>
          <h3 className="text-base md:text-lg font-bold text-gray-900">
            {topLevel?.title || perTest?.title || `AI Awareness for ${isBba ? 'BBA/MBA' : 'B.Tech'} Students`}
          </h3>
        </div>
      </div>

      {/* Short message — always visible */}
      {(topLevel?.student_message || topLevel?.intro || perTest?.content) && (
        <p className="text-sm text-gray-800 leading-relaxed mb-3">
          {topLevel?.student_message || topLevel?.intro || perTest?.content}
        </p>
      )}

      {/* Quick bullet list — core_message / practical_reality_points */}
      {(topLevel?.core_message || topLevel?.practical_reality_points)?.length > 0 && (
        <ul className="space-y-1.5 mb-3">
          {(topLevel.core_message || topLevel.practical_reality_points).slice(0, 3).map((p, i) => (
            <li key={i} className="flex items-start gap-2 text-xs text-gray-700">
              <span className="mt-1 w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0" />
              <span className="leading-relaxed">{p}</span>
            </li>
          ))}
        </ul>
      )}

      {/* Expand toggle */}
      <button
        onClick={() => setExpanded(v => !v)}
        data-testid="ai-awareness-expand"
        className="inline-flex items-center gap-1.5 text-xs font-semibold text-indigo-700 hover:text-indigo-900"
      >
        {expanded ? 'Hide full guidance' : 'Read full AI awareness guidance'}
        <ChevronDown size={13} className={`transition-transform ${expanded ? 'rotate-180' : ''}`} />
      </button>

      {expanded && (
        <div className="mt-4 pt-4 border-t border-indigo-100 space-y-4">
          {topLevel?.practical_reality_points?.length > 0 && (
            <div>
              <p className="text-[11px] font-bold tracking-widest text-gray-700 uppercase mb-2">Practical reality</p>
              <ul className="space-y-1.5">
                {topLevel.practical_reality_points.map((p, i) => (
                  <li key={i} className="text-xs text-gray-700 leading-relaxed flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />{p}</li>
                ))}
              </ul>
            </div>
          )}
          {topLevel?.student_readiness_points?.length > 0 && (
            <div>
              <p className="text-[11px] font-bold tracking-widest text-gray-700 uppercase mb-2">How to stay AI-ready</p>
              <ul className="space-y-1.5">
                {topLevel.student_readiness_points.map((p, i) => (
                  <li key={i} className="text-xs text-gray-700 leading-relaxed flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-amber-500 shrink-0" />{p}</li>
                ))}
              </ul>
            </div>
          )}
          {topLevel?.core_message?.length > 0 && (
            <div>
              <p className="text-[11px] font-bold tracking-widest text-gray-700 uppercase mb-2">Core message</p>
              <ul className="space-y-1.5">
                {topLevel.core_message.map((p, i) => (
                  <li key={i} className="text-xs text-gray-700 leading-relaxed flex items-start gap-2"><span className="mt-1 w-1.5 h-1.5 rounded-full bg-blue-500 shrink-0" />{p}</li>
                ))}
              </ul>
            </div>
          )}
          {/* BBA branch-wise changes */}
          {topLevel?.branch_wise_ai_changes && (
            <div>
              <p className="text-[11px] font-bold tracking-widest text-gray-700 uppercase mb-2">Branch-wise AI changes</p>
              <div className="grid sm:grid-cols-2 gap-2">
                {Object.entries(topLevel.branch_wise_ai_changes).slice(0, 6).map(([branch, info]) => (
                  <div key={branch} className="p-3 bg-indigo-50/60 border border-indigo-100 rounded-lg">
                    <p className="text-[10px] font-bold text-indigo-700 uppercase tracking-wider mb-1">{branch.replace(/_/g, ' ')}</p>
                    <p className="text-[11px] text-gray-700 leading-relaxed line-clamp-3">
                      {(info.automation_changes && info.automation_changes[0]) || (Array.isArray(info) ? info[0] : '')}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default QuizEngine;
